import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";

const SITE = "EvooTunisia";
const LANGS = ["fr", "ar", "en", "es"];
const OG_LOCALE = { fr: "fr_FR", ar: "ar_TN", en: "en_US", es: "es_ES" };

// Reusable per-page SEO: title, meta description, canonical, hreflang, Open Graph, Twitter, JSON-LD.
// `alternates` (optional): { [lang]: absoluteUrlOrPath } — used for pages with real translated URLs (e.g. blog).
export const Seo = ({ title, description, image, type = "website", noindex = false, jsonLd, alternates }) => {
  const { lang } = useApp();
  const { pathname } = useLocation();
  const origin = typeof window !== "undefined" ? window.location.origin : "https://evootunisia.com";
  const url = origin + pathname;
  const fullTitle = title ? `${title} — ${SITE}` : `${SITE} — Marketplace B2B huile d'olive tunisienne`;
  const img = image || `${origin}/og-default.jpg`;
  const locale = OG_LOCALE[lang] || "fr_FR";

  const resolveAlt = (v) => (v && v.startsWith("http") ? v : `${origin}${v}`);
  const altLinks = alternates
    ? Object.entries(alternates).map(([l, v]) => ({ l, href: resolveAlt(v) }))
    : LANGS.map((l) => ({ l, href: `${url}?lang=${l}` }));

  return (
    <Helmet prioritizeSeoTags>
      <html lang={lang} />
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={url} />
      {altLinks.map(({ l, href }) => (
        <link key={l} rel="alternate" hrefLang={l} href={href} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={url} />

      <meta property="og:site_name" content={SITE} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content={locale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={img} />

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};
